/**
 * CML migration service context.
 *
 * VITE_CML_SERVICE controls the provider:
 *   mock (default) — MockCmlService; seeded rows, no backend
 *   http           — HttpCmlService; calls /api/arm-data/cml/*
 */
import { createContext, useContext, type ReactNode } from 'react'
import type { ICmlService } from './ICmlService.interface'
import { MockCmlService } from './MockCmlService'
import { HttpCmlService } from './HttpCmlService'

function resolveService(): ICmlService {
  const key = (import.meta.env.VITE_CML_SERVICE as string | undefined) ?? 'mock'
  switch (key) {
    case 'http': return new HttpCmlService()
    case 'mock':
    default:     return new MockCmlService()
  }
}

const defaultService = resolveService()

const CmlServiceContext = createContext<ICmlService>(defaultService)

interface ProviderProps {
  children: ReactNode
  /** Override the service instance — used in tests. */
  service?: ICmlService
}

export function CmlServiceProvider({ children, service: override }: ProviderProps): JSX.Element {
  return (
    <CmlServiceContext.Provider value={override ?? defaultService}>
      {children}
    </CmlServiceContext.Provider>
  )
}

export function useCmlService(): ICmlService {
  return useContext(CmlServiceContext)
}
